import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiLock, FiMail, FiPhone, FiUser } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { api } from '../services/api';
import { resolveSettingMediaUrl, useSiteSettings } from '../context/SiteSettingsContext';
import { RegistrationUnavailable } from '../components/SiteUnavailable';
import './Auth.css';

const Register = () => {
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const { settings } = useSiteSettings();

  const handleChange = (event) => {
    setFormData((current) => ({ ...current, [event.target.name]: event.target.value }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('auth.passwordShort'));
      return;
    }
    if (!formData.name || !formData.email || !formData.password) {
      setError(t('auth.requiredFields'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/api/auth/register', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      });
      if (response.success && response.data?.user && response.data?.token) {
        register(response.data.user, response.data.token);
        navigate(from || (response.data.user.role === 'admin' ? '/admin-panel' : localePath('/user-panel')));
      } else {
        setError(response.message || t('auth.registerFailed'));
      }
    } catch (err) {
      setError(err.data?.message || err.message || t('auth.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!settings.status.registrationEnabled) return <RegistrationUnavailable />;

  return (
    <main className="auth-v2-page">
      <section className="auth-v2-shell">
        <div className="auth-v2-card auth-v2-card-register">
            <Link className="auth-v2-brand" to={localePath('/')}>
              <img src={resolveSettingMediaUrl(settings.general.logoUrl) || '/logo/logo-login.webp'} alt={settings.general.siteTitle} />
            </Link>
            <header className="auth-v2-heading">
              <h2>{t('auth.registerTitle')}</h2>
              <p>{t('auth.registerHint')}</p>
            </header>
            {error && <div className="auth-v2-alert is-error" role="alert">{error}</div>}
            <form onSubmit={handleSubmit} className="auth-v2-form">
              <label className="auth-v2-field" htmlFor="name"><span>{t('auth.name')}</span><div><FiUser />
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder={t('auth.name')} autoComplete="name" required />
              </div></label>
              <label className="auth-v2-field" htmlFor="phone"><span>{t('auth.phone')} <small>{t('auth.optional')}</small></span><div><FiPhone />
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('auth.phonePlaceholder')} autoComplete="tel" inputMode="tel" maxLength={20} dir="ltr" />
              </div></label>
              <label className="auth-v2-field" htmlFor="email"><span>{t('auth.email')}</span><div><FiMail />
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="example@email.com" autoComplete="email" required />
              </div></label>
              <div className="auth-v2-password-grid">
                <label className="auth-v2-field" htmlFor="password"><span>{t('auth.password')}</span><div><FiLock />
                  <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} placeholder={t('auth.passwordMinPlaceholder')} autoComplete="new-password" required />
                </div></label>
                <label className="auth-v2-field" htmlFor="confirmPassword"><span>{t('auth.confirmPassword')}</span><div><FiLock />
                  <input type="password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder={t('auth.confirmPlaceholder')} autoComplete="new-password" required />
                </div></label>
              </div>
              <div className="auth-v2-options">
                <label className="auth-v2-check"><input type="checkbox" required /><span>{t('auth.acceptRules')}</span></label>
              </div>
              <button type="submit" className="auth-v2-submit" disabled={submitting}>{submitting ? t('auth.registering') : t('auth.createAccount')}</button>
            </form>
            <footer className="auth-v2-footer"><p>{t('auth.haveAccount')} <Link to={localePath('/login')} state={from ? { from } : undefined}>{t('auth.signIn')}</Link></p></footer>
            <Link className="auth-v2-back" to={localePath('/')}><FiArrowLeft /> {t('auth.backSite')}</Link>
        </div>
      </section>
    </main>
  );
};

export default Register;
